import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCategoryProductList, getMenu } from "../services/apiService";
import noimage from "../assets/noimage.webp";
import "../styles/CategoriesPage.css";
import {
  AddItemToWishList,
  AddItemToCart,
  DeleteFromWishList,
  getProductQty,
} from "../services/Cart";
import { Notifyed } from "../services/Auth";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { showSnackbar } from "../comopnents/snackbar";
import LoadingSpinner from "../comopnents/LoadingSpinner";
// import { Notifyed } from "../services/Cart";
import { CartContext } from "../contextApi/CartContext";
import { withImageVersion } from "../contextApi/ImageVersionContext";
import CartQuantityControl from "../comopnents/CartQuantityControl";
import PurchaseOptionsModal from "../comopnents/PurchaseOptionsModal";
import {
  buildCartPurchaseData,
  buildEnhancedPurchaseData,
  getMinimumPurchaseQuantity,
  getMinOrderQty,
  getPurchaseQuantityStep,
  getSavedCartPurchaseData,
  shouldShowPurchaseModal,
} from "../utils/purchaseOptions";
import { isOutOfStock } from "../utils/stock";
import AuthModal from "../comopnents/AuthModal";
import FilterPanel from "../comopnents/FilterPanel";
import { useProductFilters } from "../hooks/useProductFilters";
import { FaStar } from "react-icons/fa";

const getCategoryImage = (category) =>
  withImageVersion(
    category?.Images?.find((image) => image?.imageurl)?.imageurl
  ) || noimage;

const CategoriesPage = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const categoryScrollRef = useRef(null);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(
    searchParams.get("category") || "",
  );
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistedProducts, setWishlistedProducts] = useState({});
  const [wishlist, setWishlist] = useState({});
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [pendingPurchaseProduct, setPendingPurchaseProduct] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingCart, setPendingCart] = useState(null);
  const [pendingWishlistProductId, setPendingWishlistProductId] =
    useState(null);
  const [viewAll, setViewAll] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );

  const [cartItems, setCartItems] = useState([]);

  const filters = useProductFilters(products);
  const displayedProducts = filters.filteredProducts;
  const {
    addToCart,
    deleteCartItems,
    itemList,
    updateQty,
    setCartQty,
    userId,
    removeFromCart,
  } = useContext(CartContext);

  const handleAddToCart = async (product, purchaseType, couponData = null) => {
    const userId = localStorage.getItem("userId");
    if (isOutOfStock(product)) {
      await handleNotifyMe(product.productid || product.dtl?.[0]?.productid);
      return;
    }

    const purchaseData = buildEnhancedPurchaseData(
      product,
      purchaseType,
      couponData,
    );
    const productId = purchaseData.productId;

    if (!userId) {
      setPendingCart({ product, purchaseType });
      setShowAuthModal(true);
      return;
    }

    try {
      const payload = {
        customerid: userId,
        productid: productId,
        qty: purchaseData.quantity,
        stock: product.stock || product.dtl?.[0]?.stock,
      };
      await AddItemToCart(payload);
      addToCart(productId, purchaseData.quantity, purchaseData);
      showSnackbar("success", "Item added to cart!");
    } catch (error) {
      console.error(
        "Add to cart error:",
        error.response?.data || error.message,
      );
      showSnackbar("error", "Failed to add item to cart!");
    }
  };

  const openPurchaseModal = (product) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCart({ product, purchaseType: "piece" });
      setShowAuthModal(true);
      return;
    }

    if (!shouldShowPurchaseModal(product)) {
      handleAddToCart(product, "piece");
      return;
    }
    setPendingPurchaseProduct(product);
    setShowPurchaseModal(true);
  };

  const confirmPurchaseType = async (purchaseType, couponData) => {
    if (!pendingPurchaseProduct) return;

    await handleAddToCart(pendingPurchaseProduct, purchaseType, couponData);
    setShowPurchaseModal(false);
    setPendingPurchaseProduct(null);
  };

  const handleNotifyMe = async (productid) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    try {
      await Notifyed({ customerid: userId, productid });
      showSnackbar("success", "We will notify you when product is available");
    } catch (error) {
      console.error("Notify me error:", error.response?.data || error.message);
      showSnackbar("error", "Failed to register notify request");
    }
  };

  const handleCartQtyChange = async (productId, newQty) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    try {
      await getProductQty({
        user_id: userId,
        productid: productId,
        qty: newQty,
      });
      setCartQty(productId, newQty);
    } catch (error) {
      console.error("API Error:", error?.message || error);
      showSnackbar("error", "Failed to update quantity");
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const data = await getMenu();
        const menuCategories = Array.isArray(data?.Data) ? data.Data : [];
        setCategories(menuCategories);

        if (!selectedCategorySlug && menuCategories.length) {
          const firstSlug =
            menuCategories[0]?.Images?.[0]?.category_slug ||
            menuCategories[0]?.category;
          setSelectedCategorySlug(firstSlug);
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const selectedCategory = useMemo(() => {
    if (!categories.length) return null;

    return (
      categories.find((category) => {
        const slug = category?.Images?.[0]?.category_slug || category?.category;
        return (
          slug === selectedCategorySlug ||
          category?.category === selectedCategorySlug
        );
      }) || categories[0]
    );
  }, [categories, selectedCategorySlug]);

  useEffect(() => {
    if (viewAll) {
      const loadAllProducts = async () => {
        try {
          setProductsLoading(true);
          const menu = await getMenu();
          const menuCategories = Array.isArray(menu?.Data) ? menu.Data : [];
          const categoryResponses = await Promise.all(
            menuCategories.map((categoryItem) =>
              getCategoryProductList(categoryItem.category).catch(() => ({
                data: [],
              })),
            ),
          );
          const allProducts = categoryResponses.flatMap((response) =>
            Array.isArray(response?.data) ? response.data : [],
          );
          const seenProductKeys = new Set();
          const uniqueProducts = allProducts.filter((product, index) => {
            const productKey =
              product?.productid ||
              product?.feeproductid ||
              product?.modelno ||
              `${product?.category || "category"}-${product?.subcategory || "subcategory"}-${index}`;
            if (seenProductKeys.has(productKey)) return false;
            seenProductKeys.add(productKey);
            return true;
          });
          setProducts(uniqueProducts);
        } catch (error) {
          console.error("Failed to load all products:", error);
          setProducts([]);
        } finally {
          setProductsLoading(false);
        }
      };
      loadAllProducts();
    } else if (selectedCategory?.category) {
      const loadProducts = async () => {
        try {
          setProductsLoading(true);
          const data = await getCategoryProductList(selectedCategory.category);
          setProducts(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
          console.error("Failed to load category products:", error);
          setProducts([]);
        } finally {
          setProductsLoading(false);
        }
      };
      loadProducts();
    }
  }, [selectedCategory?.category, viewAll]);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    if (pendingCart) {
      const { product, purchaseType } = pendingCart;
      setPendingCart(null);
      if (!shouldShowPurchaseModal(product)) {
        handleAddToCart(product, purchaseType);
      } else {
        setPendingPurchaseProduct(product);
        setShowPurchaseModal(true);
      }
    } else if (pendingWishlistProductId) {
      AddToWishList(pendingWishlistProductId);
      setPendingWishlistProductId(null);
    }
  };

  const AddToWishList = async (productid) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingWishlistProductId(productid);
      setShowAuthModal(true);
      return;
    }

    try {
      if (wishlistItems.includes(productid)) return;

      await AddItemToWishList({ productid, customerid: userId });
      setWishlistItems((prev) => [...prev, productid]);
      showSnackbar("success", "Added to wishlist!");
    } catch (error) {
      console.error("Wishlist error:", error.response?.data || error.message);
    }
  };
  const handleDeleteWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      await DeleteFromWishList({ customerid: userId, productid });
      setWishlist((prev) => ({ ...prev, [productid]: false }));
    } catch (err) {
      console.error("Error deleting from wishlist:", err);
    }
  };

  const handleCategorySelect = (category) => {
    setViewAll(false);
    filters.setSelectedSubcategory([]);
    const slug = category?.Images?.[0]?.category_slug || category?.category;
    setSelectedCategorySlug(slug);
    setSearchParams({ category: slug });
  };

  const scrollCategories = (direction) => {
    if (!categoryScrollRef.current) return;

    categoryScrollRef.current.scrollBy({
      left: direction * 260,
      behavior: "smooth",
    });
  };

  const getAverageRating = (product) => {
    const ratings = product?.rating;
    if (!ratings || !Array.isArray(ratings) || ratings.length === 0)
      return null;
    const totalWeight = ratings.reduce(
      (sum, r) => sum + (r.rating || 0) * (r.zcount || 0),
      0,
    );
    const totalCount = ratings.reduce((sum, r) => sum + (r.zcount || 0), 0);
    return totalCount > 0 ? (totalWeight / totalCount).toFixed(1) : null;
  };

  return (
    <main className="categories-page">
      <section className="categories-header">
        <h1>CATEGORIES</h1>
      </section>

      {loading ? (
        <div className="categories-loading">
          <LoadingSpinner text="Loading categories..." />
        </div>
      ) : (
        <>
          <div className="category-page-slider-container">
            <button
              type="button"
              className="category-page-arrow"
              onClick={() => scrollCategories(-1)}
              aria-label="Scroll categories left"
            ></button>

            <div
              className="category-scroll"
              aria-label="Product categories"
              ref={categoryScrollRef}
            >
              {categories.map((category) => {
                const slug =
                  category?.Images?.[0]?.category_slug || category?.category;
                const isActive =
                  !viewAll &&
                  (selectedCategory?.category === category?.category ||
                    selectedCategorySlug === slug);

                return (
                  <button
                    type="button"
                    className={`category-scroll-button ${isActive ? "active" : ""}`}
                    key={slug}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {/* <img
                      src={getCategoryImage(category)}
                      alt={category.category}
                      onError={(event) => {
                        event.currentTarget.src = noimage;
                      }}
                    /> */}
                    <span>{category.category}</span>
                  </button>
                );
              })}

              {isMobile && (
                <button
                  type="button"
                  className={`category-scroll-button category-page-view-all-btn ${viewAll ? "active" : ""}`}
                  onClick={() => {
                    setViewAll(true);
                    filters.setSelectedSubcategory([]);
                    setSelectedCategorySlug("");
                    setSearchParams({});
                  }}
                >
                  <span>View All</span>
                </button>
              )}
            </div>

            {!isMobile && (
              <button
                type="button"
                className={`category-page-view-all-btn ${viewAll ? "active" : ""}`}
                onClick={() => {
                  setViewAll(true);
                  filters.setSelectedSubcategory([]);
                  setSelectedCategorySlug("");
                  setSearchParams({});
                }}
              >
                View All
              </button>
            )}

            {/* <button
              type="button"
              className="category-page-arrow"
              onClick={() => scrollCategories(1)}
              aria-label="Scroll categories right"
            >
              
            </button> */}
          </div>
          <div></div>

          <section
            className={`category-products-panel ${isFilterOpen ? "filters-open" : ""}`}
          >
            <FilterPanel filters={filters} onOpenChange={setIsFilterOpen} />

            <div className="category-products-content">
              <div className="category-products-title">
                <div className="category-products-heading">
                  {/* <h3>{viewAll ? "All Products" : `${selectedCategory?.category} Products`}</h3> */}
                </div>
                {/* <div className="category-products-actions">
                  <span>{displayedProducts.length} items</span>
                </div> */}
              </div>

              {productsLoading ? (
                <div className="category-products-loading">
                  <LoadingSpinner text="Loading products..." />
                </div>
              ) : displayedProducts.length ? (
                <div className="category-products-grid">
                  {displayedProducts
                    .filter((product) => Number(product?.active) !== 0)
                    .map((product) => {
                      const imageUrl =
                        withImageVersion(
                          product.image1?.trim() ||
                          product.image2?.trim() ||
                          product.image3?.trim() ||
                          product.dtl?.[0]?.image1?.trim()
                        ) || noimage;
                      const netrate = Number(
                        product.netrate || product.dtl?.[0]?.netrate || 0,
                      );
                      const mrp = Number(
                        product.mrp || product.dtl?.[0]?.mrp || 0,
                      );
                      const hasNetRate = netrate > 0;
                      const discount = Number(product.discount || 0);
                      const finalPrice = discount
                        ? mrp - (mrp * discount) / 100
                        : mrp;
                      const qty =
                        itemList.find(
                          (item) =>
                            String(item.productId) ===
                            String(product.productid),
                        )?.count || 0;
                      const minorderqty =
                        product.minorderqty ||
                        product.dtl?.[0]?.minorderqty ||
                        1;
                      const stock =
                        product.stock || product.dtl?.[0]?.stock || 0;
                      const savedPurchaseData = getSavedCartPurchaseData(
                        product.productid,
                      );
                      const purchaseType =
                        savedPurchaseData?.purchaseType || "piece";

                      return (
                        <div
                          className="category-product-card"
                          key={product.productid}
                        >
                          <button
                            type="button"
                            className="category-product-image-btn"
                            onClick={() =>
                              navigate(
                                `/productdetail?productid=${product.productid}`,
                              )
                            }
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              width: "100%",
                              cursor: "pointer",
                              position: "relative",
                            }}
                          >
                            <div
                              className="bestsellers-actions"
                              style={{
                                position: "absolute",
                                top: "-12px",
                                left: "10px",
                                right: "10px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                zIndex: 2,
                              }}
                            >
                              {((stock > 0 && Number(product.netrate) > 0) ||
                                discount > 0) && (
                                <span
                                  className="discount-badge"
                                  style={{
                                    top: "20px",
                                    left: "-10px",
                                    background:
                                      "linear-gradient(135deg, #ff4d4d, #d90429)",
                                    color: "#fff",
                                    padding: "2px 4px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    lineHeight: 1,
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {stock > 0 && Number(product.netrate) > 0
                                    ? "Net Rate"
                                    : `${discount}% OFF`}
                                </span>
                              )}

                              <button
                                className="wishlist-btn"
                                style={{
                                  position: "absolute",
                                  top: "6px",
                                  right: "-6px",
                                  width: "40px",
                                  height: "40px",
                                  border: "none",
                                  borderRadius: "50%",
                                  background: "#fff",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  zIndex: 10,
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWishlist((prev) => {
                                    const updated = !prev[product.productid];
                                    if (updated)
                                      AddToWishList(product.productid);
                                    else
                                      handleDeleteWishList(product.productid);
                                    return {
                                      ...prev,
                                      [product.productid]: updated,
                                    };
                                  });
                                }}
                                aria-label={
                                  wishlist[product.productid]
                                    ? "Remove from wishlist"
                                    : "Add to wishlist"
                                }
                                type="button"
                              >
                                {wishlist[product.productid] ? (
                                  <FaHeart color="#ff4d4f" size={18} />
                                ) : (
                                  <FaRegHeart size={18} />
                                )}
                              </button>
                            </div>

                            <img
                              src={imageUrl}
                              alt={product.modelno || "Product"}
                              onError={(event) => {
                                event.currentTarget.src = noimage;
                              }}
                              style={{
                                width: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </button>
                          <div className="card-brand-row">
                            <span>
                              {product.brand || selectedCategory?.category}
                            </span>

                            {getAverageRating(product) && (
                              <span
                                className="category-product-rating"
                              >
                                <FaStar color="gold" size={18} />
                                <span
                                  className="category-product-rating-value"
                                >
                                  {getAverageRating(product)}
                                </span>
                              </span>
                            )}
                          </div>

                          <strong>
                            {(
                              product.modelno || product.displaymodelno
                            )?.toUpperCase()}
                          </strong>

                          <small>
                            {(product.subcategory || product.category)
                              ?.charAt(0)
                              .toUpperCase() +
                              (product.subcategory || product.category)
                                ?.slice(1)
                                .toLowerCase()}
                          </small>
                           {isLoggedIn && <small>Min order Qty : {product.minorderqty}</small>}

                          {mrp > 0 ? (
                            <span className="category-product-price-row">
                              <span className="category-product-mrp">
                                MRP {Number(mrp).toFixed(2)}
                              </span>

                              <span
                                className="category-product-price"
                                style={{
                                  color: "#005487",
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  display: "block",
                                }}
                              >
                                Rs.{" "}
                                {Number(
                                  hasNetRate ? netrate : finalPrice || 0,
                                ).toFixed(2)}
                              </span>
                            </span>
                          ) : (
                            <span
                              className="category-product-price-row"
                              style={{
                                color: "#005487",
                                fontSize: "15px",
                                fontWeight: "600",
                                display: "block",
                              }}
                            >
                              MRP : Coming Soon
                            </span>
                          )}
                          {stock <= 0 ? (
                            <button
                              style={{
                                width: "100%",
                                padding: "7px",
                                borderRadius: "25px",
                                border: "none",
                                fontWeight: "600",
                                backgroundColor: "#e0e0e0",
                                color: "#777777",
                                transition: "background-color 0.3s ease",
                              }}
                              onClick={() => handleNotifyMe(product.productid)}
                            >
                              Notify Me
                            </button>
                          ) : qty > 0 ? (
                            <CartQuantityControl
                              qty={qty}
                              minQty={getMinimumPurchaseQuantity(
                                product,
                                purchaseType,
                              )}
                              step={
                                purchaseType !== "piece"
                                  ? getPurchaseQuantityStep(
                                      product,
                                      purchaseType,
                                    )
                                  : undefined
                              }
                              stock={stock}
                              purchaseType={purchaseType}
                              product={product}
                              onChange={(newQty) =>
                                handleCartQtyChange(product.productid, newQty)
                              }
                              className="qty-box"
                            />
                          ) : (
                            <button
                              style={{
                                width: "100%",
                                padding: "7px",
                                borderRadius: "25px",
                                border: "none",
                                fontWeight: "600",
                                backgroundColor: "#005487",
                                color: "#ffffff",
                                transition: "background-color 0.3s ease",
                              }}
                              onClick={() => openPurchaseModal(product)}
                            >
                              Add to Cart
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="category-products-empty">
                  {viewAll
                    ? "No products found."
                    : "No products found for this category."}
                </div>
              )}
            </div>
          </section>
        </>
      )}
      {showAuthModal && (
        <AuthModal
          initialMode="login"
          onClose={() => {
            setShowAuthModal(false);
            setPendingCart(null);
            setPendingWishlistProductId(null);
          }}
          onSuccess={handleAuthSuccess}
        />
      )}

      <PurchaseOptionsModal
        isOpen={showPurchaseModal}
        product={pendingPurchaseProduct}
        onClose={() => {
          setShowPurchaseModal(false);
          setPendingPurchaseProduct(null);
        }}
        onConfirm={confirmPurchaseType}
      />
    </main>
  );
};

export default CategoriesPage;
